import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  IconButton,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // In production (ondigitalocean.app or services.knls.in), use relative URL
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '/api';
  }
  // Development fallback
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const StudentDashboard = () => {
  const { phone } = useParams();
  const [loading, setLoading] = useState(true);
  const [studentData, setStudentData] = useState(null);
  const [error, setError] = useState(null);

  const fetchStudentData = async () => {
    if (!phone) {
      setError('Phone number is required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Clean phone number - remove non-digits
      const cleanPhone = phone.replace(/\D/g, '');
      
      if (cleanPhone.length < 10) {
        setError('Invalid phone number format');
        setLoading(false);
        return;
      }

      console.log('📡 Fetching student dashboard for phone:', cleanPhone);
      console.log('📡 API URL:', `${API_BASE_URL}/students/dashboard/${cleanPhone}`);
      
      // This is a public route, no auth token needed
      const response = await axios.get(`${API_BASE_URL}/students/dashboard/${cleanPhone}`, {
        timeout: 10000, // 10 second timeout
      });

      console.log('✅ Student dashboard response:', response.data);

      if (response.data.success) {
        setStudentData(response.data.student);
      } else {
        const errorMsg = response.data.message || 'Failed to load student data';
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error('❌ Error fetching student dashboard:', err);
      console.error('❌ Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        statusText: err.response?.statusText,
      });
      
      let errorMessage = 'Failed to fetch student dashboard';
      
      if (err.response) {
        // Server responded with error
        errorMessage = err.response.data?.message || `Server error: ${err.response.status}`;
      } else if (err.request) {
        // Request made but no response
        errorMessage = 'Unable to connect to server. Please check your internet connection.';
      } else {
        // Error in request setup
        errorMessage = err.message || 'Failed to load student dashboard';
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudentData();
  }, [phone]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !studentData) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert 
          severity="error" 
          action={
            <IconButton onClick={fetchStudentData} color="inherit" size="small" title="Retry">
              <RefreshIcon />
            </IconButton>
          }
          sx={{ mb: 2 }}
        >
          {error || 'Failed to fetch student dashboard'}
        </Alert>
        {phone && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Phone: {phone}
          </Typography>
        )}
      </Box>
    );
  }

  const daysRemaining = studentData.daysRemaining;
  const isExpired = studentData.isExpired === true || (typeof daysRemaining === 'number' && daysRemaining <= 0);
  const accessSummary = studentData.courseAccessSummary || null;
  const accessStartDate = studentData.accessStartsAt || accessSummary?.starts_at || studentData.joining_date;
  const accessCourseName = accessSummary?.course_name || accessSummary?.course_code;

  return (
    <Box>
      {/* Student ID Banner */}
      {studentData.student_id && (
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 3,
            p: 3,
            mb: 3,
            color: 'white',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
          }}
        >
          <Typography variant="subtitle2" sx={{ opacity: 0.9, mb: 1 }}>
            Student ID (For Certificates & Verification)
          </Typography>
          <Typography variant="h3" fontWeight="bold" sx={{ fontFamily: 'monospace', letterSpacing: 2 }}>
            {studentData.student_id}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.8, mt: 1, display: 'block' }}>
            Enrollment: {studentData.enrollment_date || formatDate(studentData.joining_date)} at {studentData.enrollment_time || 'N/A'}
            {studentData.age && ` • Age: ${studentData.age} years`}
            {studentData.courseNamesCombined && ` • Course: ${studentData.courseNamesCombined}`}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Student Dashboard
        </Typography>
        <IconButton onClick={fetchStudentData}>
          <RefreshIcon />
        </IconButton>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                {studentData.name}
              </Typography>
              <Chip
                label={studentData.status}
                color={studentData.status === 'active' ? 'success' : 'default'}
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Enrolled Courses
              </Typography>
              {studentData.courses && studentData.courses.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {studentData.courses.map((course) => (
                    <Chip
                      key={course.course_code}
                      label={course.course_name}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              ) : (
                <Typography color="text.secondary">No courses enrolled</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Course Registered Date
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {formatDate(accessStartDate)}
              </Typography>
              {accessCourseName && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {accessCourseName}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card
            sx={{
              bgcolor: isExpired ? 'warning.light' : 'success.light',
              color: isExpired ? 'warning.contrastText' : 'success.contrastText',
            }}
          >
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Days Remaining
              </Typography>
              <Typography variant="h2" fontWeight="bold">
                {daysRemaining === null ? 'Unlimited' : `${daysRemaining ?? 0} days`}
              </Typography>
              {studentData.daysElapsed !== undefined && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  ({studentData.daysElapsed} days elapsed)
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Fee Information
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Fee
                  </Typography>
                  <Typography variant="h6" color="primary">
                    ₹{studentData.total_fee.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Paid Fee
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    ₹{studentData.paid_fee.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Due Fee
                  </Typography>
                  <Typography variant="h6" color="error.main">
                    ₹{studentData.due_fee.toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StudentDashboard;

